import { useEffect, useRef, useState } from "react";
import { track } from "../game/analytics.js";

interface CreditGroup {
  heading: string;
  lines: { label: string; detail: string; href?: string }[];
}

/**
 * Attribution + thank-you overlay. Mirrors docs/CREDITS.md (the source of
 * truth) and satisfies the Twemoji CC-BY 4.0 attribution requirement in-app.
 * Copy is intentionally em-dash-free.
 */
const CREDIT_GROUPS: CreditGroup[] = [
  {
    heading: "Source footage",
    lines: [
      {
        label: "AI-generated video",
        detail:
          "MiniMax Hailuo generated the Vet Day clip. The Dramatic Cat and Office Snack Bandit use placeholder clips.",
      },
    ],
  },
  {
    heading: "Emoji",
    lines: [
      {
        label: "Twemoji",
        detail: "Twitter, licensed CC-BY 4.0. Thanks for the open glyph set.",
        href: "https://github.com/jdecked/twemoji",
      },
    ],
  },
  {
    heading: "Fonts",
    lines: [
      {
        label: "Space Grotesk & IBM Plex Mono",
        detail: "SIL Open Font License.",
      },
    ],
  },
  {
    heading: "Audio",
    lines: [
      {
        label: "Kenney (Digital Audio, Impact, Interface)",
        detail: "CC0 public-domain music and interface stems.",
        href: "https://kenney.nl",
      },
      {
        label: "OpenGameArt: Dark Sci-Fi",
        detail: "CC0 public-domain sci-fi stems.",
      },
    ],
  },
  {
    heading: "Game",
    lines: [
      {
        label: "DESK",
        detail: "Original design and code.",
      },
    ],
  },
];

/**
 * Runtime deps + toolchain, authored by hand from desk/game/package.json
 * (not a runtime import — keep this list in sync manually when deps change).
 * License families are the well-known ones for these packages.
 */
interface LibCredit {
  label: string;
  detail: string;
  license: string;
}
const CODE_LIBS: LibCredit[] = [
  { label: "React", detail: "UI library.", license: "MIT" },
  { label: "React DOM", detail: "DOM renderer for React.", license: "MIT" },
  { label: "Vite", detail: "Dev server and production bundler.", license: "MIT" },
  { label: "TypeScript", detail: "Static types for the whole app.", license: "Apache-2.0" },
  { label: "Vitest", detail: "Unit test runner.", license: "MIT" },
];

/** v1.0.0 changelog, authored from git history — short and player-facing. */
interface ChangelogEntry {
  heading: string;
  bullets: string[];
}
const CHANGELOG: ChangelogEntry[] = [
  {
    heading: "1.0.0",
    bullets: [
      "Stickers: unlimited stickers with on-video move/resize/rotate, Fluent Emoji art, per-beat target zones, and store packs.",
      "Captions: bold styles (rainbow, red-alert, serif, clean) with an inline switcher, plus on-video move/resize/rotate.",
      "Editor: decluttered tools; removed inline explanations and chips, kept the \"How it's landing\" panel.",
    ],
  },
];

interface HowtoStep {
  heading: string;
  /** One-line summary shown under the heading, above the photo|text row. */
  oneliner: string;
  /** The fuller explanation — stands alone for text-only steps, or sits
   *  beside the photo in the row for illustrated steps. */
  text: string;
  /** Screenshot filename under /assets/howto/ — omitted for text-only steps. */
  image?: string;
  alt?: string;
}

const HOWTO_STEPS: HowtoStep[] = [
  {
    heading: "The goal",
    oneliner: "Cut the same footage into a win for very different crowds.",
    text:
      "Every reel plays to a few crowds, and each wants a different story. Your job: cut, caption, sticker, and score the SAME footage so the crowd you're serving reacts the way YOU want.",
  },
  {
    heading: "Know your crowds",
    oneliner: "Each crowd wants something different. See it before you edit.",
    text:
      "Tap the crowd faces (or \"How it's landing\") to see what each crowd wants: green = win it, red = avoid it. The spider shows how your current edit is landing against those wants.",
    image: "landing.png",
    alt: "The How it's landing panel showing crowd tabs and a spider chart of wants (green) versus backlash (red).",
  },
  {
    heading: "Track it live",
    oneliner: "\"How it's landing\" updates as you edit, in real time.",
    text:
      "Land at least one crowd to move on; land all three to complete the level and earn more followers ★. Open it from the LVL pill up top, then tap Pin to editor to keep the live spider beside you as you edit.",
    image: "editor.png",
    alt: "The top of the DESK editor with the live spider pinned beside the reel; an arrow points up to the LVL pill in the top bar that opens it.",
  },
  {
    heading: "Hints: the zones",
    oneliner: "Glowing zones during certain beats hide sticker bonuses.",
    text:
      "With the Sticker tool open, glowing zones appear on the video during certain beats. Drop the RIGHT sticker on a zone at that moment for a bonus (e.g. shades on the cat = Funny). Anywhere else, stickers are just for fun.",
    image: "zones.png",
    alt: "The video preview with a Sticker tool hint zone outlined on the subject during a beat.",
  },
  {
    heading: "Stuck? Ask a colleague",
    oneliner: "A nudge is one tap away in \"How it's landing.\"",
    text:
      "Open \"How it's landing\" and tap \"Ask a colleague\" for a nudge toward an unmet want of the crowd you are editing for: which lever, or which sticker + zone, to try. The other two crowds only ever get a read on how they are taking it, never a recipe.",
    image: "colleague.png",
    alt: "The Ask a colleague nudge, expanded to show a hint for an unmet want of the crowd being edited for.",
  },
  {
    heading: "Share with friends",
    oneliner: "Send your edit out and see if anyone can beat it.",
    text:
      "Happy with your reel? Hit Share on the results screen to send it straight to WhatsApp, Messages, or a link. Your friends can try to beat your edit.",
    image: "share.png",
    alt: "A published reel's results with the Share drawer open and the Beat My Edit share button visible.",
  },
  {
    heading: "Now go have fun",
    oneliner: "There's no single right answer here.",
    text:
      "One moment can be told a hundred ways. Express yourself, and have fun playing the villain.",
  },
];

const TAB_ORDER = ["studio", "credits", "version", "howto", "privacy"] as const;
type TabId = (typeof TAB_ORDER)[number];
const TAB_LABELS: Record<TabId, string> = {
  studio: "Studio",
  credits: "Credits",
  version: "Version",
  howto: "How to play",
  privacy: "Privacy",
};

export function AboutModal({ onClose }: { onClose: () => void }) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const [activeTab, setActiveTab] = useState<TabId>("studio");

  useEffect(() => {
    track({ name: "about_opened" });
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="about-overlay"
      data-testid="about-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="about-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="about-sheet">
        <button
          type="button"
          ref={closeRef}
          className="about-close"
          data-testid="about-close"
          aria-label="Close"
          onClick={onClose}
        >
          ✕
        </button>
        <h2 id="about-title" className="about-title">
          About DESK
        </h2>

        {/* Mobile-first horizontal scroll — 5 tabs mostly fit on a phone
            width, but the strip still scrolls rather than wraps or shrinks
            text if it doesn't. Native scrollbar is hidden (desk.css) and the
            wrapper below draws an edge fade so overflow stays legible. */}
        <div className="about-tabs-wrap">
          <div className="about-tabs" role="tablist" aria-label="About sections" data-testid="about-tabs">
            {TAB_ORDER.map((id) => {
              const active = id === activeTab;
              return (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  id={`about-tab-${id}`}
                  aria-selected={active}
                  aria-controls={`about-panel-${id}`}
                  className={`about-tab${active ? " active" : ""}`}
                  data-testid={`about-tab-${id}`}
                  onClick={() => setActiveTab(id)}
                >
                  {TAB_LABELS[id]}
                </button>
              );
            })}
          </div>
        </div>

        {activeTab === "studio" ? (
          <div
            className="about-panel"
            role="tabpanel"
            id="about-panel-studio"
            aria-labelledby="about-tab-studio"
            data-testid="about-panel-studio"
          >
            <div className="about-scroll">
              <img
                className="ripple-logo"
                data-testid="ripple-logo"
                src="/assets/brand/rippleridge-logo.png"
                alt="RippleRidge"
                width={150}
                height={95}
                decoding="async"
              />
              <p className="about-blurb">
                RippleRidge is a tiny studio that makes playful things. One
                truth, many stories. The same moment can be told a hundred
                ways. We made DESK so you can find yours. Express yourself.
              </p>
              <p className="about-thanks" data-testid="about-thanks">
                Thanks to my family, for the patience, the snacks, and being my
                first playtesters. ❤️
              </p>
              <hr className="about-divider" aria-hidden />
              <div className="about-links">
                <a
                  className="about-link-btn"
                  data-testid="ripple-site-link"
                  href="https://rippleridge.games"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  🌐 Visit RippleRidge
                </a>
                {/* No tip jar exists yet — this slot points at the community
                    instead of inventing a payment URL. */}
                <a
                  className="about-link-btn"
                  data-testid="ripple-support"
                  href="https://discord.gg/5cgeCfj9Mg"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  💬 Join the Discord
                </a>
                <a
                  className="about-link-btn"
                  data-testid="contact-link"
                  href="mailto:rippleridgegames@gmail.com"
                >
                  ✉️ Say hi / report a bug
                </a>
              </div>
              <p className="about-copyright" data-testid="about-copyright">
                © 2026 RippleRidge
              </p>
            </div>
          </div>
        ) : null}

        {activeTab === "credits" ? (
          <div
            className="about-panel"
            role="tabpanel"
            id="about-panel-credits"
            aria-labelledby="about-tab-credits"
            data-testid="about-panel-credits"
          >
            <div className="about-scroll">
              <p className="about-intro">
                DESK is built on open assets from generous creators. Every
                third-party asset below is free-licensed and credited.
              </p>
              <div className="about-groups">
                {CREDIT_GROUPS.map((group) => (
                  <section key={group.heading} className="about-group">
                    <h3 className="about-group-head">{group.heading}</h3>
                    <ul className="about-list">
                      {group.lines.map((line) => (
                        <li key={line.label} className="about-item">
                          <span className="about-item-label">{line.label}</span>{" "}
                          <span className="about-item-detail">{line.detail}</span>
                          {line.href ? (
                            <>
                              {" "}
                              <a
                                className="about-item-link"
                                href={line.href}
                                target="_blank"
                                rel="noreferrer noopener"
                              >
                                link
                              </a>
                            </>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
                <section className="about-group">
                  <h3 className="about-group-head">Code & libraries</h3>
                  <ul className="about-list">
                    {CODE_LIBS.map((lib) => (
                      <li key={lib.label} className="about-item">
                        <span className="about-item-label">{lib.label}</span>{" "}
                        <span className="about-item-detail">
                          {lib.detail} {lib.license}.
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              </div>
              <p className="about-thanks">
                Thanks to the creators and communities behind these open assets.
              </p>
            </div>
          </div>
        ) : null}

        {activeTab === "version" ? (
          <div
            className="about-panel"
            role="tabpanel"
            id="about-panel-version"
            aria-labelledby="about-tab-version"
            data-testid="about-panel-version"
          >
            <div className="about-scroll">
              {/* Hardcoded to match desk/game/package.json "version". Bump both
                  together when the package version changes. */}
              <p className="about-version-num" data-testid="about-version-num">
                v1.0.0
              </p>
              {CHANGELOG.map((entry) => (
                <section key={entry.heading} className="about-group">
                  <h3 className="about-group-head">{entry.heading}</h3>
                  <ul className="about-list">
                    {entry.bullets.map((bullet) => (
                      <li key={bullet} className="about-item about-item-detail">
                        {bullet}
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          </div>
        ) : null}

        {activeTab === "howto" ? (
          <div
            className="about-panel"
            role="tabpanel"
            id="about-panel-howto"
            aria-labelledby="about-tab-howto"
            data-testid="about-panel-howto"
          >
            <div className="about-scroll">
              <ol className="about-howto-steps">
                {HOWTO_STEPS.map((step) => (
                  <li key={step.heading} className="about-howto-step">
                    <h3 className="about-howto-heading">{step.heading}</h3>
                    <p className="about-howto-oneliner">{step.oneliner}</p>
                    {step.image ? (
                      <div className="about-howto-row">
                        <img
                          className="about-howto-photo"
                          src={`/assets/howto/${step.image}`}
                          alt={step.alt ?? step.heading}
                          loading="lazy"
                        />
                        <p className="about-howto-text">{step.text}</p>
                      </div>
                    ) : (
                      <p className="about-howto-body">{step.text}</p>
                    )}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        ) : null}

        {activeTab === "privacy" ? (
          <div
            className="about-panel"
            role="tabpanel"
            id="about-panel-privacy"
            aria-labelledby="about-tab-privacy"
            data-testid="about-panel-privacy"
          >
            <div className="about-scroll">
              <p className="about-intro">
                <strong>Your privacy.</strong> DESK runs in your browser. Your
                game progress and preferences are saved locally on your
                device, not on our servers, and no account is required to
                play.
              </p>
              <p className="about-intro">
                To improve the game we collect <strong>anonymous, aggregated</strong>{" "}
                usage: which levels people play, how they interact, where
                they get stuck, through a privacy-focused analytics tool. It
                isn't tied to your identity: no name, email, or account, and
                no tracking cookies.
              </p>
              <p className="about-intro">
                Some technical data (like your IP address) is necessarily
                processed by our hosting provider and analytics provider to
                deliver and secure the site, each under their own privacy
                policies. We don't sell your data.
              </p>
              <p className="about-intro">
                DESK isn't directed at children under 13, and we don't
                knowingly collect their data. A future update may add an{" "}
                <strong>optional</strong> account to save and share your reels
                and add friends. Always opt-in, and we'll update this note
                before it ships.
              </p>
              <p className="about-intro">
                You can clear DESK's local data anytime via your browser
                settings. Questions? Reach us at [contact]. We may update this
                note as the game evolves.
              </p>
              {/* TODO(user): fill real contact; have this reviewed before
                  launch if you want a binding policy */}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
